import { useEffect, useRef, useState } from 'react';
import { sameVehicle } from '../domain';
import { useConfiguratorState } from '../store';
import { useRestoreStore } from './restoreStore';
import type { SavedCarConfiguration, UnavailableReason } from './types';

const sameSet = (a: number[], b: number[]): boolean => {
	const left = new Set(a);
	const right = new Set(b);
	return left.size === right.size && [...left].every((id) => right.has(id));
};

export function useConfigurationRestore(): {
	reason: UnavailableReason | null;
	record: SavedCarConfiguration | null;
	dismiss: () => void;
} {
	const incoming = useRestoreStore((s) => s.incoming);
	const engine = useConfiguratorState((s) => s.engine);
	const defaultsSeeded = useConfiguratorState((s) => s.defaultsSeeded);
	const [reason, setReason] = useState<UnavailableReason | null>(null);
	const verified = useRef<string | null>(null);

	useEffect(() => () => useRestoreStore.getState().clear(), []);

	useEffect(() => {
		if (!incoming || !engine || !defaultsSeeded) return;
		if (incoming.vehicleType === 'stock') return;
		if (verified.current === incoming.id) return;

		const state = useConfiguratorState.getState();
		const wanted = incoming.payload.progress;
		if (!sameVehicle(state.vehicle, wanted.vehicle)) return;

		verified.current = incoming.id;

		if (!sameSet(state.committedPicks, wanted.selectedOptions)) {
			setReason('options_invalid');
			return;
		}

		const ref = wanted.vehicle!;
		state
			.validator(
				ref.eurotaxCode,
				ref.vehicleKind,
				engine.wireIds(),
				ref.year,
				ref.month
			)
			.then((res) => {
				if (res.errors.length > 0) {
					console.error('restore-time validate disagreement', {
						sent: engine.wireIds(),
						errors: res.errors,
					});
					setReason('options_invalid');
				}
			})
			.catch(() => setReason('options_invalid'));
	}, [incoming, engine, defaultsSeeded]);

	return { reason, record: incoming, dismiss: () => setReason(null) };
}
